import React, { useState } from 'react';
import { Target, CircleDollarSign, Swords, ShieldCheck, Lightbulb, Zap, BrainCircuit, CheckCircle, XCircle, TrendingUp, Briefcase, Wrench, Rocket } from 'lucide-react';
import './Report.css';

const ReportCard = ({ icon, title, children, className = '' }) => (
  <div className={`report-card ${className}`}>
    <div className="card-header">
      {icon}
      <h3>{title}</h3>
    </div>
    <div className="card-content">
      {children}
    </div>
  </div>
);

const SWOTCard = ({ title, points, icon, type }) => (
    <div className={`swot-card ${type}`}>
        <div className="card-header">
            {icon}
            <h4>{title}</h4>
        </div>
        <ul>
            {points && points.map((point, index) => <li key={index}>{point}</li>)}
        </ul>
    </div>
);

const Report = ({ report, idea }) => {
  const [brandingSuggestions, setBrandingSuggestions] = useState(report.brandingSuggestions || []);
  const [isLoadingMoreNames, setIsLoadingMoreNames] = useState(false);

  const fetchMoreNames = async () => {
    setIsLoadingMoreNames(true);
    try {
      const response = await fetch('https://start-up-idea-validator-polpewjq2-salahs-projects-e5953328.vercel.app/api/more_names', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idea }),
      });
      const newNames = await response.json();
      setBrandingSuggestions(prev => [...prev, ...newNames]);
    } catch (error) {
      console.error("Failed to fetch more names:", error);
    }
    setIsLoadingMoreNames(false);
  };

  if (!report || report.error) {
    return (
        <ReportCard icon={<XCircle size={24} />} title="Analysis Failed">
            <p>{report?.error || "An unknown error occurred during analysis."}</p>
        </ReportCard>
    );
  }

  const {
    summary = "No summary available.",
    targetAudience = {},
    marketAnalysis = {},
    monetizationModels = [],
    swotAnalysis = {},
    feasibilityAssessment = {},
    technologyRecommendations = {},
    nextSteps = [],
    competitorAnalysis = { competitors: [] }
  } = report;


  return (
    <div className="report-grid">
      <ReportCard icon={<Lightbulb size={24} />} title="Idea Summary" className="full-width">
        <p>{summary}</p>
      </ReportCard>

      <ReportCard icon={<Target size={24} />} title="Target Audience">
        <p><strong>{targetAudience.description}</strong></p>
        <ul>
            {(targetAudience.demographics || []).map((demo, i) => <li key={i}>{demo}</li>)}
        </ul>
      </ReportCard>

      <ReportCard icon={<TrendingUp size={24} />} title="Market Analysis">
        <p><strong>Market Size:</strong> {marketAnalysis.size}</p>
        <p><strong>Trends:</strong> {marketAnalysis.trends}</p>
        <p><strong>Growth:</strong> {marketAnalysis.growthProjection}</p>
      </ReportCard>
      
      <ReportCard icon={<CircleDollarSign size={24} />} title="Monetization Models">
        <ul>
          {monetizationModels.map((model, index) => <li key={index}><strong>{model.model}:</strong> {model.description}</li>)}
        </ul>
      </ReportCard>

      <ReportCard icon={<Briefcase size={24} />} title="Feasibility Assessment">
        <div className="score-container">
            <span className="score-value">{feasibilityAssessment.score}/10</span>
            <p className="score-rationale">Overall Feasibility</p>
        </div>
        <p><strong>Technical:</strong> {feasibilityAssessment.technical}</p>
        <p><strong>Operational:</strong> {feasibilityAssessment.operational}</p>
      </ReportCard>

      <div className="report-card full-width">
        <div className="card-header">
            <BrainCircuit size={24} />
            <h3>SWOT Analysis</h3>
        </div>
        <div className="swot-grid">
            <SWOTCard title="Strengths" points={swotAnalysis.strengths} icon={<Zap size={20} />} type="strengths" />
            <SWOTCard title="Weaknesses" points={swotAnalysis.weaknesses} icon={<Swords size={20} />} type="weaknesses" />
            <SWOTCard title="Opportunities" points={swotAnalysis.opportunities} icon={<Lightbulb size={20} />} type="opportunities" />
            <SWOTCard title="Threats" points={swotAnalysis.threats} icon={<ShieldCheck size={20} />} type="threats" />
        </div>
      </div>

      <ReportCard icon={<Wrench size={24} />} title="Technology Recommendations">
        <p><strong>Core Stack:</strong> {(technologyRecommendations.coreStack || []).join(', ')}</p>
        <p><strong>AI Components:</strong> {(technologyRecommendations.aiComponents || []).join(', ')}</p>
      </ReportCard>

      <ReportCard icon={<Rocket size={24} />} title="Next Steps">
        <ul>
            {(nextSteps || []).map((step, i) => <li key={i}>{step}</li>)}
        </ul>
      </ReportCard>

      {/* Competitor Analysis - Temporarily commented out */}
      {/*
      <div className="report-card full-width">
        <div className="card-header">
            <Swords size={24} />
            <h3>Competitor Analysis ({competitorAnalysis.count || 0})</h3>
        </div>
        <div className="competitors-list">
            {visibleCompetitors.length > 0 ? visibleCompetitors.map((c, index) => (
                <div key={index} className="competitor-item">
                    <h4>{c.name}</h4>
                    <a href={c.url} target="_blank" rel="noopener noreferrer">{c.url}</a>
                    <p className="overview">{c.overview}</p>
                    {c.recent_info && c.recent_info.length > 0 && (
                        <div className="recent-info">
                            <h5>Recent Activity:</h5>
                            <ul>
                                {c.recent_info.map((info, i) => (
                                    <li key={i}>
                                        <a href={info.url} target="_blank" rel="noopener noreferrer">{info.title}</a>
                                        <span> ({info.source} - {new Date(info.date).toLocaleDateString()})</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )) : <p>No direct competitors found based on the analysis.</p>}
            {(competitorAnalysis.competitors || []).length > 3 && (
                <button onClick={() => setShowAllCompetitors(!showAllCompetitors)} className="show-more-btn">
                    {showAllCompetitors ? 'Show Less' : `Show ${competitorAnalysis.competitors.length - 3} More...`}
                </button>
            )}
        </div>
      </div>
      */}
      
      <div className="report-card full-width">
        <div className="card-header">
            <Lightbulb size={24} />
            <h3>Branding Suggestions</h3>
        </div>
        <div className="name-list">
            {brandingSuggestions.map((s, index) => (
                <div key={index} className="name-item">
                    <span className="name">{s.name}</span>
                    <div className="domain-status">
                        <span>{s.domain}</span>
                        {s.available ? <CheckCircle size={18} color="#22C55E" /> : <XCircle size={18} color="#EF4444" />}
                    </div>
                </div>
            ))}
        </div>
        <button onClick={fetchMoreNames} className="show-more-btn" disabled={isLoadingMoreNames}>
            {isLoadingMoreNames ? 'Generating...' : 'Suggest More'}
        </button>
      </div>
    </div>
  );
};

export default Report;
